import {
  Component,
  effect,
  inject,
  input,
  InputSignal,
  OnDestroy,
  output,
  OutputEmitterRef,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { AppendTestIdPipe } from '../../../../../../pipes/append-test-id/append-test-id.pipe';

@Component({
  selector: 'aw-time-picker',
  imports: [FormsModule, ReactiveFormsModule, AppendTestIdPipe],
  templateUrl: './time-picker.component.html',
})
export class TimePickerComponent implements OnDestroy {
  public readonly id: InputSignal<string> = input.required<string>();
  public readonly date: InputSignal<Date | undefined> = input<Date | undefined>(
    undefined
  );
  public readonly label: InputSignal<string> = input('Zeit ab');

  private readonly formBuilder: FormBuilder = inject(FormBuilder);

  public readonly onHourChange: OutputEmitterRef<string> = output<string>();
  public readonly onMinuteChange: OutputEmitterRef<string> = output<string>();

  public readonly hours: FormControl<string> =
    this.formBuilder.nonNullable.control('00');
  public readonly minutes: FormControl<string> =
    this.formBuilder.nonNullable.control('00');

  private readonly subscription: Subscription = new Subscription();

  constructor() {
    effect(() => {
      const date: Date | undefined = this.date();
      if (date) {
        const hours: string = date.getHours() + '';
        const minutes: string = date.getMinutes() + '';
        this.hours.setValue(hours.length < 2 ? '0' + hours : hours);
        this.minutes.setValue(minutes.length < 2 ? '0' + minutes : minutes);
      }
    });
    this.subscription.add(
      this.hours.valueChanges.subscribe((value: string) => {
        const isNumber = new RegExp('^[0-9]*$');
        let cleanedValue: string = value
          .split('')
          .filter((character: string) => isNumber.test(character))
          .join('');
        if (value.length > 2) {
          cleanedValue = value[0] + value[1];
        }
        if (+cleanedValue > 23) {
          cleanedValue = '23';
        }
        this.hours.setValue(cleanedValue, { emitEvent: false });
      })
    );
    this.subscription.add(
      this.minutes.valueChanges.subscribe((value: string) => {
        const isNumber = new RegExp('^[0-9]*$');
        let cleanedValue: string = value
          .split('')
          .filter(character => isNumber.test(character))
          .join('');
        if (value.length > 2) {
          cleanedValue = value[0] + value[1];
        }
        if (+cleanedValue > 59) {
          cleanedValue = '59';
        }
        this.minutes.setValue(cleanedValue, { emitEvent: false });
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  protected setHours(e: any): void {
    const cleanedValue: string = this.cleanHours(e.target.value);
    this.hours.setValue(cleanedValue, { emitEvent: false });
    this.onHourChange.emit(cleanedValue);
  }

  protected cleanHours(value: string): string {
    let cleanedValue: string = value;
    if (+cleanedValue > 23) {
      cleanedValue = '00';
    }
    if (+cleanedValue < 10 && +cleanedValue > 0 && cleanedValue.length < 2) {
      cleanedValue = '0' + cleanedValue;
    } else if (+cleanedValue < 1) {
      cleanedValue = '00';
    }
    return cleanedValue;
  }

  protected increaseHours(e: any): void {
    const value: number = +e.target.value + 1;
    const cleanedValue: string = this.cleanHours(value + '');
    this.hours.setValue(cleanedValue, { emitEvent: false });
    this.onHourChange.emit(cleanedValue);
  }

  protected decreaseHours(e: any): void {
    const value: number = +e.target.value - 1;
    const cleanedValue: string = this.cleanHours(value + '');
    this.hours.setValue(cleanedValue, { emitEvent: false });
    this.onHourChange.emit(cleanedValue);
  }

  protected setMinutes(e: any): void {
    const cleanedValue: string = this.cleanMinutes(e.target.value);
    this.minutes.setValue(cleanedValue, { emitEvent: false });
    this.onMinuteChange.emit(cleanedValue);
  }

  protected cleanMinutes(value: string): string {
    let cleanedValue: string = value;
    if (+cleanedValue > 59) {
      cleanedValue = '00';
    }
    if (+cleanedValue < 10 && +cleanedValue > 1 && cleanedValue.length < 2) {
      cleanedValue = '0' + cleanedValue;
    } else if (+cleanedValue < 1) {
      cleanedValue = '00';
    }
    return cleanedValue;
  }

  protected increaseMinutes(e: any): void {
    const value: number = +e.target.value + 1;
    const cleanedValue: string = this.cleanMinutes(value + '');
    this.minutes.setValue(cleanedValue, { emitEvent: false });
    this.onMinuteChange.emit(cleanedValue);
  }

  protected decreaseMinutes(e: any): void {
    const value: number = +e.target.value - 1;
    const cleanedValue: string = this.cleanMinutes(value + '');
    this.minutes.setValue(cleanedValue, { emitEvent: false });
    this.onMinuteChange.emit(cleanedValue);
  }
}
